<script setup>
import { computed, ref } from 'vue'
import { Link, router } from '@inertiajs/vue3'
import Button from 'primevue/button'
import FormsPickerPopover from './FormsPickerPopover.vue'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'

const MAX_VISIBLE = 5

const props = defineProps({
    reservationForms: { type: Array, default: () => [] },
    reservations:     { type: Array, default: () => [] },
    visibleFormIds:   { type: Set,   default: () => new Set() },
})

const emit = defineEmits(['toggle-form'])

function formCount(id) {
    return props.reservations.filter(r => r.id_reservation_forms === id).length
}

const sortedForms  = computed(() => [...props.reservationForms].sort((a, b) => formCount(b.id) - formCount(a.id)))
const visibleForms = computed(() => sortedForms.value.slice(0, MAX_VISIBLE))
const hiddenCount  = computed(() => Math.max(0, props.reservationForms.length - MAX_VISIBLE))
const hasMore      = computed(() => hiddenCount.value > 0)

const pickerOpen      = ref(false)
const pickerAnchor    = ref(null)
const showMoreBtnRef  = ref(null)

function openPicker() {
    pickerAnchor.value = showMoreBtnRef.value?.$el?.getBoundingClientRect() ?? null
    pickerOpen.value = true
}
</script>

<template>
    <div class="flex flex-col gap-0">
        <div class="flex items-center justify-between pb-1 px-1">
            <span class="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{{ $t('reservations.forms_section_title') }}</span>
            <Link href="/app/forms" class="text-[11px] text-blue-500 font-medium hover:text-blue-700">
                {{ $t('reservations.forms_manage') }}
            </Link>
        </div>

        <button
            v-for="form in visibleForms"
            :key="form.id"
            type="button"
            class="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors w-full text-left"
            :class="visibleFormIds.has(form.id) ? '' : 'opacity-40'"
            @click="emit('toggle-form', form.id)"
        >
            <i
                class="!text-[12px] flex-shrink-0 w-4 transition-colors"
                :class="visibleFormIds.has(form.id) ? 'pi pi-eye' : 'pi pi-eye-slash text-neutral-300'"
                :style="visibleFormIds.has(form.id) ? { color: getFormColor(form) } : {}"
            />
            <div class="w-2 h-2 rounded-sm flex-shrink-0" :style="{ background: getFormColor(form) }" />
            <span class="flex-1 text-[12.5px] text-neutral-800 dark:text-neutral-200 font-medium truncate min-w-0">
                {{ form.name }}
            </span>
            <span class="text-[11px] text-neutral-400 dark:text-neutral-500 tabular-nums">{{ formCount(form.id) }}</span>
        </button>

        <div class="mt-0.5">
            <Button
                v-if="hasMore"
                ref="showMoreBtnRef"
                text
                size="small"
                @click="openPicker"
                class="w-full !justify-start !gap-1.5 !text-[11.5px] !font-medium !text-neutral-500 dark:!text-neutral-400 !rounded-lg"
            >
                <i class="pi pi-th-large !text-[10px]" />
                {{ $t('reservations.forms_show_all') }}
                <span class="ml-auto bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-300 rounded px-1.5 py-px text-[10px] font-semibold">
                    +{{ hiddenCount }}
                </span>
            </Button>
        </div>
    </div>

    <FormsPickerPopover
        :open="pickerOpen"
        :forms="reservationForms"
        :visible-form-ids="visibleFormIds"
        :reservations="reservations"
        :anchor-rect="pickerAnchor"
        @close="pickerOpen = false"
        @toggle-form="(id) => emit('toggle-form', id)"
    />
</template>
